import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vehicleRegistrationKeys } from '../services/queryKeys';
import { fetchVehicleAmendments, VEHICLE_AMENDMENTS_PER_PAGE } from '../services/vehicleRegistration';
import { isClientError } from './useVehicleRegistrationQuery';

/** Page state is keyed by vehicle id, so opening another plan starts on page 1 and
 *  never shows the previous vehicle's rows as placeholder data. */
export function useVehicleAmendments(vehicleId: string | undefined) {
    const [pageState, setPageState] = useState({ vehicleId, page: 1 });
    const requestedPage = pageState.vehicleId === vehicleId ? pageState.page : 1;
    const setPage = useCallback((next: number) => setPageState({ vehicleId, page: next }), [vehicleId]);

    const query = useQuery({
        queryKey: vehicleRegistrationKeys.amendmentsPage(vehicleId ?? '', requestedPage, VEHICLE_AMENDMENTS_PER_PAGE),
        queryFn: () =>
            fetchVehicleAmendments(vehicleId!, { page: requestedPage, perPage: VEHICLE_AMENDMENTS_PER_PAGE }),
        enabled: !!vehicleId,
        retry: (count, error) => !isClientError(error) && count < 2,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[1] === (vehicleId ?? '') ? previousData : undefined,
    });

    // Clamps the page state, not just the reported value, so the query refetches at
    // the clamped page rather than showing an out-of-range page's empty body.
    const lastPage = query.data?.meta.lastPage;

    useEffect(() => {
        if (lastPage !== undefined && requestedPage > lastPage) {
            setPageState({ vehicleId, page: lastPage });
        }
    }, [vehicleId, lastPage, requestedPage]);

    return { query, page: requestedPage, setPage };
}
